import { router } from '@inertiajs/react'
import type { NavigableWorker } from '@/frontend/types/inertia'
import { persistSelectedWorker, type SelectedWorkerStorageContext } from '@/frontend/layouts/worker-first/useSelectedWorker'
import { agentChatHref } from '@/frontend/layouts/worker-first/agentAccess'
import { isSectionAvailable, workerSectionFromPath } from '@/frontend/layouts/worker-first/workerSections'

// A aba Chat do portal (Iteração 3, B/C): /dashboard/digital_workers/:id/chat.
// A rota antiga /dashboard/agent_chats segue como 302 pra cá, então cobrimos as
// duas formas ao decidir se "o lugar atual é o chat".
const CHAT_PATH_RE = /^\/dashboard\/digital_workers\/[^/]+\/chat$/
function isChatPath(path: string): boolean {
  return path.startsWith('/dashboard/agent_chats') || CHAT_PATH_RE.test(path)
}

function isWorkerLandingPath(path: string): boolean {
  const workerId = path.match(/^\/dashboard\/digital_workers\/([^/]+)$/)?.[1]
  return Boolean(workerId && workerId !== 'new')
}

export interface WorkerNavigationTarget {
  href: string
  preserveState: boolean
  preserveScroll: boolean
}

/**
 * Pure destination resolver shared by imperative switchers (palette/shortcuts)
 * and real links (the Agents sidebar). Keeping it pure lets links retain native
 * browser behavior without reimplementing the preserve-place rules.
 */
export function workerNavigationTarget(
  worker: NavigableWorker,
  currentUrl: string,
): WorkerNavigationTarget {
  const path = currentUrl.split('?')[0]

  if (isChatPath(path)) {
    return {
      href: agentChatHref(worker.id),
      preserveState: true,
      preserveScroll: true,
    }
  }

  // The landing is Perfil > Visão geral. Switching agents from it should keep
  // the user in Perfil instead of unexpectedly jumping into Chat.
  if (isWorkerLandingPath(path)) {
    return {
      href: `/dashboard/digital_workers/${worker.id}`,
      preserveState: false,
      preserveScroll: false,
    }
  }

  const section = workerSectionFromPath(path)
  if (section && isSectionAvailable(section, worker)) {
    return {
      href: `/dashboard/digital_workers/${worker.id}/${section}`,
      preserveState: false,
      preserveScroll: false,
    }
  }

  const chatReady = worker.provisioning_status === 'ready' && worker.probe_connect_ready
  return {
    href: chatReady ? agentChatHref(worker.id) : `/dashboard/digital_workers/${worker.id}`,
    preserveState: false,
    preserveScroll: false,
  }
}

/**
 * Canonical agent-switch navigation (A.2, SZ-1580 "trocar de agente preserva o
 * lugar"). Single shared owner of the semantics for the sidebar agent links
 * and command palette (⌘K / ⌘1..9, SZ-1594). Callers handle their own
 * no-op/close/UI concerns.
 *
 * - On Chat, only the URL param changes: preserveState re-addresses the
 *   mounted page instead of remounting it (fresh props flow in and
 *   useAgentChatController resets the conversation when the worker changes),
 *   so composer drafts and scroll survive the switch (SZ-1560/SZ-1580).
 * - On a worker section page, the switch stays in the same section of the new
 *   agent. Gated sections fall back to the worker landing when the new
 *   agent's instance is not ready — the same gating WorkerNav applies to its
 *   links (the server would redirect anyway).
 * - Anywhere else there is no "place" to preserve, and the conversation is
 *   the shell (SZ-1602, B.1): the switch opens the new agent's chat —
 *   preserving the ?ui=legacy fallback — when the chat bridge is reachable
 *   (same gating as the old SZ-1570 toggle). Agents without a ready bridge
 *   land on their Painel, where provisioning/setup lives.
 */
export function visitWorkerPreservingPlace(
  worker: NavigableWorker,
  currentUrl: string,
  storageContext?: SelectedWorkerStorageContext,
) {
  persistSelectedWorker(worker.id, storageContext)
  const target = workerNavigationTarget(worker, currentUrl)
  router.visit(target.href, {
    preserveState: target.preserveState,
    preserveScroll: target.preserveScroll,
  })
}
